import React, { FC, useState, useEffect, useMemo,useCallback } from 'react';
import { CircularProgress, Grid, Theme, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TimeSlots from './components/timeSlots';
import { useTheme } from '@mui/material/styles';
import moment from 'moment-timezone';
import { useStore } from 'store/StoreProvider';
import NextAvailableSchedule from './components/nextSchedule';
import Line from '@mui/icons-material/HorizontalRule';
import { useSession } from 'hooks/SessionProvider';
import useWeekSchedules from 'hooks/useWeekSchedules';
import { BackendDateFormat } from 'utils/constants';
import useGetParsedDateTime from 'hooks/useGetParsedDateTime';

const useStyles = makeStyles((theme: Theme) => ({
  dateBox: {
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      width: '19%',
    },
    width: '90%',
    paddingLeft: '2px',
    paddingRight: '2px',
    [theme.breakpoints.down('md')]: {
      width:"96%",
      paddingLeft: '5px',
      paddingRight: '5px',
      display:"flex",
      justifyContent:"center"
    },
  },
  dateMonth: {
    fontSize: '12px',
    color: '#2b2b2b',
    fontWeight: '550',
    [theme.breakpoints.down('md')]: {
      fontSize: '10px',
    },
  },

  progressBar:{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '170px',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10, // Ensures the progress bar is on top
    [theme.breakpoints.down('md')]:{
      height: '100px',
    },
      [theme.breakpoints.up('md')]:{
        height: '170px',
    },
  }
}));

interface Props {
  doctorId: number;
  addressID: number;
}

const ScheduleTimes: FC<Props> = ({ doctorId, addressID }) => {
  const classes = useStyles();
  const store = useStore();
  const data = store.getData();
  const {startedDate}=useSession();
  const [startDate, setStartDate] = useState<string | undefined>(startedDate);
  const [foundSchedules, setFoundSchedules] = useState<any[]>([]);
  const [isShowAll, setIsShowAll] = useState<boolean>(false);
  const getSchedules=data?.FilterdSchedules?.[`doctor_${doctorId}`]?.[`address_${addressID}`]
  const weekSchedules=useWeekSchedules(getSchedules,startedDate)
  const displayedSlots = useMemo(() => isShowAll ? foundSchedules : foundSchedules.slice(0, 4), [isShowAll, foundSchedules]);
  const timeSlots = useMemo(() => displayedSlots.length > 0 ? displayedSlots.length : 4, [displayedSlots]);
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  useEffect(() => {
    setStartDate(startedDate);
  }, [startedDate]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const daysToShow = isSmallScreen ? 1 : data?.DaysToShow; // Number of days to show  
  const startDateTime = useGetParsedDateTime(data?.start_datetime); 
  const getAppointmentTypeID = store?.getSelectedDateTime()?.appointmentTypeID ;

  const getAppointmentType = (id: number): any => {
    return data?.AppointmentTypes.find((appt: { id: number; }) => appt.id === id);
  }

  const duration = getAppointmentType(getAppointmentTypeID).duration;

  useEffect(() => {
    setLoading(true);
  }, [weekSchedules]);

  const handleSlotLoadComplete = useCallback(() => {
    setLoading(false);
  }, [weekSchedules, displayedSlots.length]);

  const renderDates = () => {
    let foundSchedule = false;
    const dates: JSX.Element[] = [];

    for (let i = 0; i < daysToShow; i++) {
      const currentDate = startDate ? moment.parseZone(startDate,BackendDateFormat) : moment();
      currentDate.add(i, 'days');

      const matchingSchedules = weekSchedules.filter((item: any) => {
        const scheduleDate = moment.parseZone(item.checkin_time, BackendDateFormat);
        const scheduleEndTime = moment.parseZone(item.checkout_time, BackendDateFormat).clone()
        .subtract(data?.hours_to_skip_schedules,"hours")
        .subtract(duration,"minutes");
        return currentDate.isSame(scheduleDate, 'day') && scheduleEndTime.isAfter(startDateTime);  //get only schedule where current date is match and checkout time is after from current time
    });

      if (matchingSchedules.length > 0) {
        foundSchedule = true;
        dates.push(
          <Grid item key={i} className={classes.dateBox}>
            <TimeSlots matchSchedules={matchingSchedules} setFoundSchedules={setFoundSchedules} doctorId={doctorId} addressID={addressID}  onLoadComplete={handleSlotLoadComplete}loading={loading} duration={duration}></TimeSlots>
          </Grid>
        );
      } else if(!loading) {
        dates.push(
          <Grid item key={i} className={classes.dateBox}>
            <Grid>
              {[...Array(timeSlots)].map((_, index) => (
                <Grid item key={index} sx={{ height: '35px', marginBottom: '8px', display: 'flex', justifyContent: 'center', paddingTop: "7px", alignItems: "center" }}>
                  <Line sx={{color:"#d2d5df"}}></Line>
                </Grid>
              ))}
            </Grid>
          </Grid>
        );
      }
    }
    if (!foundSchedule) {
      loading && handleSlotLoadComplete();
      return (
       <Grid sx={{ minHeight: { xs: "95px" },width:"100%", paddingLeft:{sm:"10px"},paddingRight:{sm:"10px"}}}>
         <NextAvailableSchedule 
          schedules={getSchedules} 
          doctorId={doctorId} 
          duration={duration}
        />
       </Grid>
      );
    }

    return dates;
  };

  
  return (
    <Grid sx={{ display: 'flex', width: '100%', position: 'relative',justifyContent:"center" }}>
      {loading && (
        <Grid className={classes.progressBar}>
          <CircularProgress />
        </Grid>
      )}
      {renderDates()}
    </Grid>
  );
  
};

export default ScheduleTimes;
